import type { IconProps } from '@sr/ui-library';
import { Icon, SidebarMenuButton, SidebarMenuItem, cn } from '@sr/ui-library';
import type { ComponentProps, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { CountBadge } from './CountBadge';
import { PrefetchingNavLink } from './PrefetchingNavLink';

export interface SidebarLinkProps {
  badge?: ReactNode;
  icon?: IconProps['icon'];
  iconClassName?: string;
  isPrefetching?: boolean;
  label: ReactNode;
  onClick?: ComponentProps<'button'>['onClick'];
  rel?: string;
  target?: ComponentProps<'a'>['target'];
  to?: string;
}

/**
 * Route items use React Router by default. They render as plain anchors when
 * the URL is external or when the caller explicitly wants a new tab, which
 * avoids React Router intercepting links that should open outside the app.
 */
const isExternalUrl = (to: string, target?: ComponentProps<'a'>['target']) =>
  to.startsWith('http') || target === '_blank';

/**
 * Shared look for every sidebar entry. Built on top of shadcn's
 * `SidebarMenuButton`, so we override its default gray accent fill back to
 * transparent. `group/link` lets the red left-edge indicator and the label
 * react to active/hover; padding/gap match the historical MSR3 spacing rather
 * than shadcn's defaults. The global indigo/underline link styling and the
 * `outline-none` box are neutralized by the `[data-sidebar-link]` reset in
 * index.css — hence no `!important` here. Every rendered leaf element must
 * carry the `data-sidebar-link` attribute for that reset to apply.
 */
export const SIDEBAR_LINK_CLASS = cn(
  'group/link relative flex items-center gap-1 px-3 py-0.5',
  'hover:bg-transparent active:bg-transparent',
  'focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-primary',
  '!no-underline',
);

const SidebarLinkContent = ({
  badge,
  icon,
  iconClassName,
  label,
}: Pick<SidebarLinkProps, 'badge' | 'icon' | 'iconClassName' | 'label'>) => (
  <>
    {/* Red left-edge indicator. `NavLink` sets `aria-current="page"` on the
        active route, so active/hover styling is driven purely by CSS. */}
    <span
      className={cn(
        'absolute left-0 top-1/2 h-4 w-1 -translate-y-1/2 rounded-full bg-primary transition-opacity',
        'opacity-0 group-hover/link:opacity-30 group-aria-[current=page]/link:opacity-100',
      )}
    />
    {icon && (
      <Icon
        className={cn(
          'mr-1 shrink-0 transition-colors',
          'text-foreground/50 group-hover/link:text-foreground group-aria-[current=page]/link:text-foreground',
          iconClassName,
        )}
        icon={icon}
        size={'sm'}
      />
    )}

    <span className="text-sm font-light text-foreground/70 group-hover/link:text-foreground group-aria-[current=page]/link:text-foreground">
      {label}
    </span>
    {badge && <CountBadge>{badge}</CountBadge>}
  </>
);

export const SidebarLink = ({
  badge,
  icon,
  iconClassName,
  isPrefetching = false,
  label,
  onClick,
  rel,
  target,
  to,
}: SidebarLinkProps) => {
  const isExternal = to ? isExternalUrl(to, target) : false;
  const secureRel = target === '_blank' && !rel ? 'noopener noreferrer' : rel;

  const content = (
    <SidebarLinkContent
      label={label}
      badge={badge}
      icon={icon}
      iconClassName={iconClassName}
    />
  );

  if (!to) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          className={SIDEBAR_LINK_CLASS}
          data-sidebar-link
          type="button"
          onClick={onClick}
        >
          {content}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  if (isExternal) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild className={SIDEBAR_LINK_CLASS}>
          <a data-sidebar-link href={to} rel={secureRel} target={target}>
            {content}
          </a>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  const LinkComponent = isPrefetching ? PrefetchingNavLink : NavLink;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild className={SIDEBAR_LINK_CLASS}>
        <LinkComponent
          data-sidebar-link
          rel={secureRel}
          target={target}
          to={to}
        >
          {content}
        </LinkComponent>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
